import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOfServiceOpen, setTermsOfServiceOpen] = useState(false);
  const [kvkkOpen, setKvkkOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = 'Hotel Yapay Zeka Çözümleri | Oteller için AI Asistan, OTA Ranker, Rakip Analizi';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Oteller için yapay zeka destekli WhatsApp asistan, OTA sıralama takibi, rakip fiyat analizi ve itibar yönetimi. 132 dilde 7/24 misafir desteği. Resepsiyon yükünü %80 azaltın.');
    }
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="bg-white text-gray-900 font-sans antialiased overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 z-50 w-full transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <nav className="px-6 md:px-12 py-4 max-w-7xl mx-auto flex items-center justify-between" aria-label="Ana navigasyon">
          <div className="flex items-center gap-3">
            <a href="/" aria-label="Hotel Yapay Zeka Çözümleri Ana Sayfa">
              <h1 className="sr-only">Hotel Yapay Zeka Çözümleri</h1>
              <img src="https://public.readdy.ai/ai/img_res/f353b4d7-dacd-40f7-a8e4-5449d615ae6c.png" alt="Hotel Yapay Zeka Çözümleri Logo" className="h-12 w-auto" />
            </a>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-gray-900 hover:text-teal-600">Ana Sayfa</a>
            <Link to="/apps" className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-gray-900 hover:text-teal-600">Uygulamalar</Link>
            <a href="#sss" onClick={(e) => { e.preventDefault(); scrollToSection('sss'); }} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-gray-900 hover:text-teal-600">SSS</a>
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-sm font-medium transition-colors cursor-pointer whitespace-nowrap text-gray-900 hover:text-teal-600">İletişim</a>
          </div>

          <div className="flex gap-3">
            <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="hidden sm:flex h-10 px-6 items-center justify-center rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold transition-all shadow-lg shadow-teal-600/30 cursor-pointer whitespace-nowrap">
              <span>Ücretsiz Demo</span>
            </a>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-gray-900 cursor-pointer" aria-label="Menüyü aç/kapat" aria-expanded={mobileMenuOpen}>
              <i className="ri-menu-line text-2xl text-gray-900"></i>
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200/50 shadow-lg">
            <nav className="flex flex-col p-6 gap-4" aria-label="Mobil navigasyon">
              <a href="#home" onClick={(e) => { e.preventDefault(); scrollToSection('home'); }} className="text-left text-gray-900 hover:text-teal-600 font-medium cursor-pointer">Ana Sayfa</a>
              <Link to="/apps" className="text-left text-gray-900 hover:text-teal-600 font-medium cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Uygulamalar</Link>
              <a href="#sss" onClick={(e) => { e.preventDefault(); scrollToSection('sss'); }} className="text-left text-gray-900 hover:text-teal-600 font-medium cursor-pointer">SSS</a>
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-left text-gray-900 hover:text-teal-600 font-medium cursor-pointer">İletişim</a>
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative pt-16 pb-20 lg:pt-24 lg:pb-32 overflow-hidden" aria-labelledby="hero-title">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-teal-50 via-white to-white"></div>

          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
              {/* Left Content */}
              <article className="flex-1 flex flex-col gap-6 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wider mx-auto lg:mx-0 w-fit">
                  <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  Yapay Zeka Destekli
                </div>
                
                <h2 id="hero-title" className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-tight tracking-tight">
                  Resepsiyonun Yükünü <strong className="text-teal-600">%80 Azaltan</strong> Yapay Zeka Asistanı
                </h2>
                
                <p className="text-lg md:text-xl text-gray-900 leading-relaxed">
                  <strong>Hotel Yapay Zeka Çözümleri</strong> sayesinde misafirleriniz hiç bir uygulama indirmeden doğrudan <strong className="text-teal-600">WhatsApp</strong> üzerinden destek alabilirler. Personel verimliğiniz artarken <strong>misafir memnuniyeti</strong> en üst seviyeye ulaşır! Üstelik kurulumu tamamen ücretsiz.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 mt-4">
                  <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="h-12 px-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-base font-bold shadow-xl shadow-teal-600/25 transition-all transform hover:-translate-y-1 flex items-center justify-center cursor-pointer whitespace-nowrap">
                    Ücretsiz Deneyin
                  </a>
                  <a href="#solutions" onClick={(e) => { e.preventDefault(); scrollToSection('solutions'); }} className="h-12 px-8 rounded-lg bg-white border border-gray-200 text-gray-900 text-base font-bold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center">
                    Demo Hesap
                  </a>
                </div>
              </article>

              {/* Right Phone Mockup */}
              <div className="flex-1 relative w-full max-w-[400px] lg:max-w-none flex justify-center">
                <div className="relative z-10 w-[280px] sm:w-[320px] bg-white rounded-[2.5rem] border-[8px] border-gray-900 shadow-2xl overflow-hidden aspect-[9/19]">
                  <div className="w-full h-full bg-[#e5ddd5] flex flex-col relative">
                    {/* WhatsApp Header */}
                    <div className="bg-[#008069] h-16 flex items-center px-4 pt-4 text-white gap-3 shadow-md">
                      <i className="ri-arrow-left-line text-xl"></i>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                          <i className="ri-robot-2-line text-sm"></i>
                        </div>
                        <div className="flex flex-col">
                          <span className="font-bold text-sm">Otel Asistanı</span>
                          <span className="text-[10px] opacity-80">Çevrimiçi</span>
                        </div>
                      </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="flex-1 p-4 space-y-3 overflow-hidden">
                      <div className="bg-[#d9fdd3] p-2 rounded-lg rounded-tr-none shadow-sm max-w-[85%] ml-auto text-xs">
                        Merhaba
                      </div>
                      <div className="bg-white p-2 rounded-lg rounded-tl-none shadow-sm max-w-[85%] self-start text-xs">
                        Merhaba! 👋 Grand Otel'e hoş geldiniz. Size nasıl yardımcı olabilirim?
                      </div>
                      <div className="bg-[#d9fdd3] p-2 rounded-lg rounded-tr-none shadow-sm max-w-[85%] ml-auto text-xs">
                        Merhaba, bu hafta sonu için 2 kişilik oda bakıyorum.
                      </div>
                      <div className="bg-white p-2 rounded-lg rounded-tl-none shadow-sm max-w-[85%] self-start text-xs">
                        Harika! 📅 12-14 Ağustos tarihleri için deniz manzaralı Deluxe odamız müsait. Gecelik fiyatı 150€. Rezervasyonu tamamlayayım mı?
                      </div>
                      <div className="bg-[#d9fdd3] p-2 rounded-lg rounded-tr-none shadow-sm max-w-[85%] ml-auto text-xs">
                        Evet, lütfen. Kahvaltı dahil mi?
                      </div>
                      <div className="bg-white p-2 rounded-lg rounded-tl-none shadow-sm max-w-[85%] self-start text-xs">
                        Evet, zengin açık büfe kahvaltımız fiyata dahildir. 🥐☕
                      </div>
                    </div>

                    {/* WhatsApp Input */}
                    <div className="h-14 bg-[#f0f2f5] flex items-center px-2 gap-2">
                      <i className="ri-add-line text-gray-500 text-xl"></i>
                      <div className="flex-1 h-9 bg-white rounded-full"></div>
                      <i className="ri-mic-line text-[#008069] text-xl"></i>
                    </div>
                  </div>
                </div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[80%] bg-teal-500/20 blur-3xl -z-10 rounded-full"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-gray-900 py-16 border-y border-white/5" aria-label="İstatistikler">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="text-white mb-2">
                  <i className="ri-translate-2 text-5xl" aria-hidden="true"></i>
                </div>
                <p className="text-5xl font-black text-white">132+</p>
                <p className="text-gray-400 font-medium text-sm">Desteklenen Dil</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="text-white mb-2">
                  <i className="ri-customer-service-2-line text-5xl" aria-hidden="true"></i>
                </div>
                <p className="text-5xl font-black text-white">7/24</p>
                <p className="text-gray-400 font-medium text-sm">Kesintisiz Hizmet</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="text-white mb-2">
                  <i className="ri-cloud-line text-5xl" aria-hidden="true"></i>
                </div>
                <p className="text-5xl font-black text-white">%100</p>
                <p className="text-gray-400 font-medium text-sm">Bulut Tabanlı</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="text-white mb-2">
                  <i className="ri-shield-check-line text-5xl" aria-hidden="true"></i>
                </div>
                <p className="text-5xl font-black text-white">%99.9</p>
                <p className="text-gray-400 font-medium text-sm">Güvenilirlik</p>
              </div>
            </div>
          </div>
        </section>

        {/* Workflow Section */}
        <section className="py-24 bg-gray-50" aria-labelledby="workflow-title">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="workflow-title" className="text-4xl md:text-5xl font-black text-gray-900 mb-6">İş Akışı Şeması</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                <strong>Yapay Zeka Çözümlerimizin</strong> nasıl çalıştığını ve <strong>otel operasyonlarınızla</strong> nasıl entegre olduğunu görün.
              </p>
            </div>

            <div className="flex justify-center">
              <figure className="w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden p-4 md:p-6">
                <img 
                  src="https://static.readdy.ai/image/73e643dee18fcd4ce852476cc145d102/2297b717cbf2693ad732a8c204c12557.jpeg" 
                  alt="Hotel Yapay Zeka İş Akışı Şeması - Misafir iletişiminden personel yönetimine kadar tüm süreçlerin görsel anlatımı" 
                  title="Hotel Yapay Zeka İş Akışı"
                  className="w-full h-auto cursor-pointer hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                  onClick={(e) => {
                    const img = e.currentTarget;
                    const modal = document.createElement('div');
                    modal.className = 'fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer';
                    modal.onclick = () => modal.remove();
                    const modalImg = document.createElement('img');
                    modalImg.src = img.src;
                    modalImg.className = 'max-w-full max-h-full object-contain';
                    modal.appendChild(modalImg);
                    document.body.appendChild(modal);
                  }}
                />
                <figcaption className="sr-only">Hotel Yapay Zeka sisteminin iş akışı diyagramı</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-24 bg-white" aria-labelledby="services-title">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="services-title" className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Hizmetlerimiz</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                <strong>Otel işletmeniz</strong> için özel olarak tasarlanmış <strong>yapay zeka destekli hizmetler</strong> ile operasyonlarınızı optimize edin.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-red-100 text-red-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-alarm-warning-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Acil Durum Yönetimi</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Yangın, sağlık sorunu veya teknik arıza durumlarında personeli ve misafirleri anında bilgilendirir. Kritik durumlarda hızlı müdahale sağlar.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-calendar-check-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Akıllı Rezervasyon</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Misafirler otele gelmeden oda tercihleri, transfer ve özel isteklerini otomatik olarak toplar. <strong>Rezervasyon sürecini</strong> kolaylaştırır.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-global-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Çok Dilli İletişim</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  <strong>132 dilde</strong> misafirlerinizle kendi dillerinde hatasız iletişim kurun. Dil bariyerlerini ortadan kaldırın ve global hizmet sunun.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-message-3-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Otomatik Mesajlaşma</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Check-in öncesi karşılama, konaklama sırası anketler ve check-out sonrası teşekkür mesajları ile <strong>misafir deneyimini</strong> zenginleştirin.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-tools-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Teknik Destek Otomasyonu</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Oda sorunlarını otomatik algılar ve teknik servise iş emri oluşturur. Bakım süreçlerini hızlandırır ve <strong>misafir memnuniyetini</strong> artırır.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-restaurant-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Restoran Yönetimi</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Oda servisi siparişlerini ve restoran rezervasyonlarını otomatik olarak alır. Mutfak ve servis ekibinizle entegre çalışır.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-map-pin-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Yerel Rehber Asistanı</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Misafirlerin çevredeki gezilecek yerler, restoranlar ve aktiviteler hakkındaki sorularına anında cevap verir.
                </p>
              </article>

              <article className="group p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:border-teal-300 transition-all hover:shadow-xl cursor-pointer">
                <div className="w-14 h-14 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <i className="ri-dashboard-line text-3xl" aria-hidden="true"></i>
                </div>
                <h4><a href="#contact" className="text-xl font-bold text-gray-900 mb-4 block hover:text-teal-600">Analitik Dashboard</a></h4>
                <p className="text-gray-600 leading-relaxed">
                  Gerçek zamanlı istatistikler, misafir geri bildirimleri ve performans raporları ile işletmenizi daha iyi yönetin.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Solutions Section */}
        <section id="solutions" className="py-24 bg-gray-50 relative overflow-hidden" aria-labelledby="solutions-title">
          <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-16">
              <div className="flex-1 space-y-8">
                <h2 className="text-4xl md:text-5xl font-black text-gray-900">
                  Hotel Yapay Zeka Çözümleri ile<br />
                  <span className="text-teal-600 border-b-4 border-teal-300">Tüm Kontrol Sizde</span>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Tüm mesajlaşmayı tek ekrandan yönetin. Misafir talepleri, acil durumlar, personel performansı ve canlı yapay zeka konuşma akışı elinizin altında. Detaylı raporlarla hizmet kalitenizi artırın.
                </p>
                <ul className="space-y-5">
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-900 font-medium text-lg">Anlık konuşma takibi ve müdahale imkanı</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-900 font-medium text-lg">Departman atamaları ile iş yükünü optimize edin</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-900 font-medium text-lg">Planlı mesajlar ile misafir deneyimini kişiselleştirin</span>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-gray-900 font-medium text-lg">Detaylı raporlama ve analiz araçları</span>
                  </li>
                </ul>
              </div>

              <div className="flex-1 w-full relative">
                <div className="rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden transform hover:scale-105 transition-all duration-500">
                  <div className="bg-gray-100 border-b border-gray-200 px-6 py-4 flex items-center gap-3">
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                  </div>
                  <div className="p-0">
                    <img src="https://hotelai.io/storage/app-panel.png" alt="Dashboard Preview" className="w-full h-auto" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-teal-600 text-white overflow-hidden relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/30 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-700/30 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
          
          <div className="max-w-5xl mx-auto px-6 md:px-12 text-center relative z-10">
            <div className="w-24 h-24 mx-auto mb-8 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
              <i className="ri-robot-2-line text-6xl text-white"></i>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6">Yapay Zeka Asistanınız İle Tanışın</h2>
            <p className="text-xl text-white/95 leading-relaxed max-w-3xl mx-auto mb-12">
              7/24 çalışan, hiç yorulmayan ve sürekli öğrenen akıllı asistanınız. Misafirlerinizin tüm taleplerini anında karşılar, personel yükünüzü azaltır ve hizmet kalitenizi artırır.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-left">
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/15 transition-all">
                <i className="ri-brain-line text-4xl mb-4"></i>
                <h3 className="font-bold text-xl mb-3 text-white">Sürekli Öğrenir</h3>
                <p className="text-sm text-white/85 leading-relaxed">
                  Otelinizdeki değişiklikleri ve yeni hizmetleri anında öğrenir, kendini sürekli geliştirir.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/15 transition-all">
                <i className="ri-time-line text-4xl mb-4"></i>
                <h3 className="font-bold text-xl mb-3 text-white">Asla Uyumaz</h3>
                <p className="text-sm text-white/85 leading-relaxed">
                  Gece vardiyasında resepsiyonun en büyük yardımcısı. 7/24 kesintisiz hizmet sunar.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 hover:bg-white/15 transition-all">
                <i className="ri-heart-line text-4xl mb-4"></i>
                <h3 className="font-bold text-xl mb-3 text-white">Profesyonel</h3>
                <p className="text-sm text-white/85 leading-relaxed">
                  Marka dilinize uygun konuşur, her zaman nazik ve yardımseverdir.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Her Ölçekteki Otel İçin Esnek Planlar</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                İhtiyacınıza uygun paketi seçin, hemen kullanmaya başlayın.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Başlangıç Plan */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 hover:shadow-xl transition-all">
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Başlangıç</h3>
                  <p className="text-gray-600 text-sm">Butik oteller ve pansiyonlar için.</p>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-gray-900">₺990</span>
                    <span className="text-gray-600">/ay</span>
                  </div>
                </div>

                <button onClick={() => scrollToSection('contact')} className="w-full h-12 px-6 rounded-lg bg-white border-2 border-teal-600 text-teal-600 text-base font-bold hover:bg-teal-50 transition-all mb-8 cursor-pointer whitespace-nowrap">
                  İlk Ay Ücretsiz Dene
                </button>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">1.000 Mesaj (30 Gün Geçerli)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">132 Dil Desteği</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Yapay Zeka Asistan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Planlı mesajlar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Email destek</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">48 Adet QR Sticker</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Ücretsiz Kurulum</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Yıllık Alımda 1 Ay Hediye</span>
                  </li>
                </ul>
              </div>

              {/* Profesyonel Plan */}
              <div className="bg-teal-600 rounded-2xl border-4 border-teal-500 p-8 hover:shadow-2xl transition-all relative transform scale-105">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-gray-900 px-6 py-1.5 rounded-full text-sm font-bold whitespace-nowrap">
                  En Popüler
                </div>
                
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-white mb-2">Profesyonel</h3>
                  <p className="text-white/90 text-sm">Şehir otelleri ve zincirler için.</p>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-white">₺1.990</span>
                    <span className="text-white/90">/ay</span>
                  </div>
                </div>

                <button onClick={() => scrollToSection('contact')} className="w-full h-12 px-6 rounded-lg bg-white text-teal-600 text-base font-bold hover:bg-gray-50 transition-all mb-8 cursor-pointer whitespace-nowrap">
                  Hemen Başla
                </button>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">2.000 Mesaj (30 Gün Geçerli)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">132 Dil Desteği</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">Gelişmiş Otel Asistan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">Planlı mesajlar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">Öncelikli destek</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">48 Adet QR Sticker</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">Ücretsiz Kurulum</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-white text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-white text-sm">Yıllık Alımda 2 Ay Hediye</span>
                  </li>
                </ul>
              </div>

              {/* Kurumsal Plan */}
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-8 hover:shadow-xl transition-all">
                <div className="mb-6">
                  <h3 className="text-2xl font-black text-gray-900 mb-2">Kurumsal</h3>
                  <p className="text-gray-600 text-sm">Resort oteller ve büyük gruplar için.</p>
                </div>
                
                <div className="mb-8">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-black text-gray-900">₺3.990</span>
                    <span className="text-gray-600">/ay</span>
                  </div>
                </div>

                <button onClick={() => scrollToSection('contact')} className="w-full h-12 px-6 rounded-lg bg-gray-900 text-white text-base font-bold hover:bg-gray-800 transition-all mb-8 cursor-pointer whitespace-nowrap">
                  İletişime Geç
                </button>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">10.000 Mesaj (30 Gün Geçerli)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">132 Dil Desteği ve Özel Entegrasyon</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Gelişmiş Otel Asistan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Planlı mesajlar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">7/24 telefon desteği</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">96 Adet QR Sticker</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Ücretsiz Kurulum</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-teal-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="text-gray-700 text-sm">Yıllık Alımda 2 Ay Hediye</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Security Section */}
            <div className="mb-20">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mb-2">
                    <i className="ri-shield-check-line text-3xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">KVKK Uyumlu</h3>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-2">
                    <i className="ri-lock-line text-3xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">Uçtan Uca Şifreleme</h3>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center mb-2">
                    <i className="ri-apps-2-line text-3xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">Uygulamalar</h3>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-xl bg-teal-100 text-teal-600 flex items-center justify-center mb-2">
                    <i className="ri-code-s-slash-line text-3xl"></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm">WhatsApp API</h3>
                </div>
              </div>
            </div>

            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Müşterilerimiz Ne Diyor?</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Türkiye'nin önde gelen otel zincirlerinden butik otellere kadar yüzlerce işletme bize güveniyor.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "Hotel Yapay Zeka Çözümleri sayesinde resepsiyon yükümüz %75 azaldı. Misafirlerimiz artık gece yarısı bile anında cevap alabiliyor. Yatırımın karşılığını ilk ayda aldık."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                    AY
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Ahmet Yılmaz</p>
                    <p className="text-sm text-gray-500">Genel Müdür, Grand Otel İstanbul</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "132 dil desteği bizim için oyun değiştirici oldu. Artık Çinli, Rus ve Arap misafirlerimizle kendi dillerinde sorunsuz iletişim kurabiliyoruz. Müşteri memnuniyeti rekor seviyede!"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                    ZK
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Zeynep Kaya</p>
                    <p className="text-sm text-gray-500">İşletme Sahibi, Boutique Hotel Antalya</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "Teknik destek otomasyonu harika çalışıyor. Misafir bir sorun bildirdiğinde sistem otomatik iş emri oluşturuyor. Müdahale süremiz yarıya indi. Kesinlikle tavsiye ediyorum!"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                    MÖ
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Mehmet Özkan</p>
                    <p className="text-sm text-gray-500">Operasyon Müdürü, Resort Hotel Bodrum</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "Dashboard'daki analitik araçlar sayesinde misafir davranışlarını çok daha iyi anlıyoruz. Hangi hizmetlerin talep gördüğünü görebiliyor ve stratejimizi buna göre şekillendiriyoruz."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-lg">
                    EA
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Elif Arslan</p>
                    <p className="text-sm text-gray-500">Pazarlama Direktörü, City Hotel Ankara</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "Kurulum çok kolay ve hızlıydı. Ekip son derece profesyonel. Sistemin kullanımı da oldukça basit, personelimiz hemen adapte oldu. Gerçekten mükemmel bir çözüm!"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-lg">
                    CY
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Can Yıldırım</p>
                    <p className="text-sm text-gray-500">Genel Müdür, Seaside Hotel İzmir</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all">
                <div className="flex items-center gap-1 mb-6">
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                  <i className="ri-star-fill text-yellow-400 text-xl"></i>
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "Otomatik mesajlaşma özelliği harika! Check-in öncesi ve sonrası mesajlar misafirlerimize çok profesyonel bir izlenim bırakıyor. Online yorumlarımız gözle görülür şekilde iyileşti."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-pink-600 flex items-center justify-center text-white font-bold text-lg">
                    SÇ
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">Selin Çelik</p>
                    <p className="text-sm text-gray-500">Müşteri İlişkileri Müdürü, Mountain Lodge</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 bg-gray-50" aria-labelledby="contact-title">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h2 id="contact-title" className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Hemen Başlayın</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                <strong>Ücretsiz demo</strong> için formu doldurun, size en kısa sürede dönüş yapalım.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-12">
              <form className="space-y-6" data-readdy-form id="contact-form" action="https://readdy.ai/api/form/d5q8hbmtc768qf4bq6og" method="POST">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="+90 5XX XXX XX XX"
                    />
                  </div>

                  <div>
                    <label htmlFor="hotel" className="block text-sm font-medium text-gray-700 mb-2">
                      Otel Adı *
                    </label>
                    <input
                      type="text"
                      id="hotel"
                      name="hotel"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                      placeholder="Otel adınız"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="rooms" className="block text-sm font-medium text-gray-700 mb-2">
                    Oda Sayısı
                  </label>
                  <select
                    id="rooms"
                    name="rooms"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm"
                  >
                    <option value="">Seçiniz</option>
                    <option value="1-20">1-20 oda</option>
                    <option value="21-50">21-50 oda</option>
                    <option value="51-100">51-100 oda</option>
                    <option value="101-200">101-200 oda</option>
                    <option value="200+">200+ oda</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Mesajınız
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-sm resize-none"
                    placeholder="Bize nasıl yardımcı olabiliriz?"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maksimum 500 karakter</p>
                </div>

                <button
                  type="submit"
                  className="w-full h-14 px-8 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-base font-bold shadow-lg shadow-teal-600/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <span>Ücretsiz Demo Talep Et</span>
                  <i className="ri-send-plane-fill"></i>
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* SSS Section */}
        <section id="sss" className="py-24 bg-white" aria-labelledby="sss-title">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="sss-title" className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Sıkça Sorulan Sorular</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Yapay Zeka Çözümleri hakkında merak ettiğiniz tüm soruların cevabı burada.
              </p>
            </div>

            <div className="space-y-6">
              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>🏨 Yapay Zeka Nedir?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Yapay Zeka, konaklama tesislerine yapay zeka destekli 132 dilde satış ve servis kabiliyeti kazandıran, otel operasyonlarını dijital bir ekosisteme dönüştüren teknoloji platformudur. Bünyesindeki Yapay Zeka uygulamalarıyla misafir iletişiminden gelir yönetimine kadar tüm süreçleri tek bir merkezden yönetir.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>📈 Yapay Zeka Asistanı Otelinize Nasıl Katkı Sağlar?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <div className="mt-4 text-gray-600 leading-relaxed">
                  <p className="mb-4">
                    Yapay Zeka Asistanınız, kalıplaşmış ve tekrarlayan tüm soruları (Wi-Fi şifresi, kahvaltı saatleri, transfer ücretleri, yerel gezi önerileri vb.) sizin adınıza <strong className="text-gray-900">7 gün 24 saat, 132 dilde</strong> anında cevaplar.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                      <span><strong className="text-gray-900">Dil Baskısı Ortadan Kalkar:</strong> Resepsiyonistlerin sürekli dil engeliyle mücadele etme ve aynı soruları defalarca yanıtlama zorunluluğunu sona erdirir.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                      <span><strong className="text-gray-900">Personel Verimliliği Artar:</strong> Resepsiyonist, sadece acil durumlar ve kritik misafir etkileşimleri için kontrolü eline alır. Geri kalan tüm rutin iletişim yükü YZ'ye devredilir.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-line text-teal-600 flex-shrink-0 mt-1"></i>
                      <span><strong className="text-gray-900">Operasyonel Süreklilik:</strong> Gece vardiyasında bile misafirleriniz asla cevapsız kalmaz.</span>
                    </li>
                  </ul>
                </div>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>✨ Yapay Zeka Asistanın Temel Yetenekleri Nelerdir?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <div className="mt-4 text-gray-600 leading-relaxed">
                  <p className="mb-4">
                    Asistanınız, sadece cevap vermekle kalmaz, tesisinizin gelirini ve misafir memnuniyetini artıran çok yönlü bir çözüm sunar:
                  </p>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <i className="ri-calendar-schedule-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Planlı Mesajlar Gönderir</h4>
                        <p className="text-sm">Yapay Zeka Asistan, misafirlerin özel günlerini kutlamayı, check-out hatırlatmaları göndermeyi veya yeni sezon kampanyalarını duyurmayı otomatikleştirir. Bu Asistan, personelinizin manuel takibine gerek kalmadan, doğru zamanda doğru mesajın iletilmesini sağlayarak misafirlerinizle duygusal bağınızı sürekli canlı tutar.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-dashboard-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Dashboard Panel</h4>
                        <p className="text-sm">Tüm WhatsApp misafir iletişimini, personelin müdahale ettiği sohbetleri ve YZ'nin performans metriklerini tek bir merkezi arayüzde toplar. Operasyonel kontrolü en üst seviyeye çıkarır. Yakında ek modüller sayesinde tüm iletişim tek panelde olacak.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-translate-2 text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Çok Dilli Destek Verir</h4>
                        <p className="text-sm">Misafirlerinizi anlık olarak 132 dilde anlayıp cevaplayarak dil bariyeri kaynaklı sorunları ortadan kaldırır. Yabancı misafirlerinizin dillerinde kesintisiz hizmet almasını garanti ederken, resepsiyonistlerin stresini sıfırlar.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-alarm-warning-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Acil Durum Yönetimi</h4>
                        <p className="text-sm">Yapay Zeka, "Yangın", "Su sızıntısı" veya "Acil sağlık" gibi kritik kelimeleri anında tespit eder. Resepsiyon ve Acil Durum WhatsApp hattına anlık, yüksek öncelikli uyarı mesajı göndererek hızlı müdahaleyi garanti altına alır.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-team-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Personel Verimliliği Sağlar</h4>
                        <p className="text-sm">Rutin bilgi taleplerini (Wi-Fi, kahvaltı saati vb.) tamamen devralarak personel yükünü %80 azaltır. Personeliniz enerjisini daha karmaşık ve gelir getirici yüz yüze etkileşimlere yönlendirir.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-tools-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Teknik Destek Verir</h4>
                        <p className="text-sm">Misafirlerin teknik sorularına (klima, TV vb.) detaylı yanıtlar sunar. Teknik ekibin basit kullanım hataları için odaya çıkma ihtiyacını azaltır. Çözülemeyen durumlarda ilgili departmana bildirir.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-map-pin-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Keşif Asistanı Sunar</h4>
                        <p className="text-sm">Otele ulaşım konusunda adım adım tarif verir. Toplu taşıma hatlarını paylaşarak resepsiyonistlerin rutin yol tarifi yükünü alır ve misafirlerin stresini azaltır.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-calendar-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Ön Rezervasyon Talebi Alma</h4>
                        <p className="text-sm">Rezervasyon taleplerini algılar ve kritik verileri (isim, tarih, kişi sayısı) toplayarak Resepsiyona yönlendirir. Satış ekibine sıcak talep (lead) sağlar.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-money-dollar-circle-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Otel İçi Satışları Artırır</h4>
                        <p className="text-sm">Müsaitliğe göre ücretli hizmetleri (Spa, Masaj vb.) proaktif olarak önerir. Misafirleri pasif alıcı konumundan çıkararak ek gelir sağlar ve RevPAR oranını artırır.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-bus-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Tur & Transfer Satabilir</h4>
                        <p className="text-sm">Transfer ve tur taleplerini detaylı yanıtlar. Talepleri ilgili departmanlara veya acentelere yönlendirerek yeni bir komisyon ve ek gelir akışı oluşturur.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-hotel-bed-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Oda Tiplerini Anlatır</h4>
                        <p className="text-sm">Oda tipleri ve olanakları hakkında detaylı bilgi sunar. Talebe göre en uygun odayı önererek rezervasyon kararını hızlandırır ve satış sürecini destekler.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-navigation-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Ulaşım Bilgisi Sunar</h4>
                        <p className="text-sm">Otele ulaşım konusunda adım adım tarif verir. Toplu taşıma hatlarını paylaşarak resepsiyonistlerin rutin yol tarifi yükünü alır ve misafirlerin stresini azaltır.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-user-settings-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Personel Takibi Yapar</h4>
                        <p className="text-sm">Temizlik veya arıza taleplerinin takibini ve onayını sağlar. Operasyonel şeffaflığı artırır ve taleplerin unutulma riskini sıfırlar.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-line-chart-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Gelişmiş Rapor Sunar</h4>
                        <p className="text-sm">Mesajlardaki duygu analizini yapar. Olumsuz bir eğilim tespit ederse, kötü bir yorum yazılmadan önce durumu çözmesi için ilgili departmanı uyarır.</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <i className="ri-magic-line text-teal-600 flex-shrink-0 mt-0.5"></i>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Ve Daha Fazlası</h4>
                        <p className="text-sm">Yapay Zeka asistan, sektör profesyonellerinin geri bildirimleriyle sürekli gelişiyor. Yeni özellikler için her zaman talepte bulunabilirsiniz.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>🤝 Biz Kimiz?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Biz, sizden biri olarak, konaklama sektörünün içinden gelen bir ekibiz. Amacımız, hızla gelişen teknolojileri kullanarak, sektörün kalıplaşmış sorunlarını en yüksek kalitede çözebilmektir. Yapay Zeka çözümlerimizle otellerin operasyonel verimliliğini artırırken, misafir deneyimini zirveye taşıyoruz.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Kurulum ne kadar sürer?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Entegrasyon sadece 5 dakika sürer. WhatsApp Business hesabınızı bağlayın, otel bilgilerinizi girin ve Akıllı Otel Asistanınız hemen kullanmaya başlayın.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>WhatsApp Business hesabım yoksa ne yapmalıyım?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Sorun değil! Size WhatsApp Business hesabı oluşturma konusunda adım adım rehberlik ediyoruz. Destek ekibimiz tüm süreçte yanınızda. Talep etmeniz durumunda tüm kurulumu ücretsiz olarak biz yapabiliriz.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Fiyatlandırma nasıl çalışıyor?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Yapay Zeka Çözümleri kredilendirme sistemiyle çalışır. Başlangıç paketi ₺990'dan başlıyor ve size aylık 1000 Gelen/Giden mesaj kredisi tanımlıyor. İhtiyacınıza uygun olan paketi seçebilirsiniz. Kış aylarında daha az kullanım olduğunda Başlangıç, Yaz aylarında yoğun sezonda Profesyone veya Kurumsal pakete geçmenizi tavsiye ederiz. Ek ücret yoktur, Kurulum tamamen ücretsizdir.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Yapay Zeka asistan hangi dilleri destekliyor?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Yapay Zeka asistanımız 132 dilde otomatik yanıt verebilir. Misafirinizin dilini otomatik algılar ve o dilde yanıt verir.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Verilerim güvende mi?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Evet! Tüm verileriniz SSL/TLS şifreleme ile korunur. KVKK ve Meta API gerekliliklerine tam uyumluyuz. Verileriniz sadece sizin otel işletmeniz için kullanılır.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Ücretsiz deneme süresi var mı?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Evet! 30 gün boyunca geçerli 500 krediniz ile ücretsiz deneme yapabilirsiniz. Kredi kartı bilgisi gerekmez. Deneme süresi sonunda beğenirseniz ödemeye başlarsınız. Deneme süresi boyunca size destek vereceğiz.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Mevcut PMS sistemimle entegre olur mu?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Şu anda manuel veri girişi ile çalışıyoruz. Ancak API entegrasyonu için Kurumsal paketimizde özel çözümler sunuyoruz. Detaylar için bizimle iletişime geçin.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Destek hizmeti sunuyor musunuz?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Elbette! Tüm paketlerde email destek sunuyoruz. Profesyonel pakette öncelikli destek, Kurumsal pakette ise 7/24 telefon desteği mevcuttur.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>İptal etmek istersem ne yapmalıyım?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  İstediğiniz zaman dashboard'dan tek tıkla iptal edebilirsiniz. Kısmi iade yapılmaz ancak mevcut dönem sonuna kadar hizmet kullanmaya devam edebilirsiniz. Yada Asistanı durdurak mesajlara manuel cevap verebilirsiniz. Kredinizin gitmemesi için bizimle iletişime geçebilirsiniz.
                </p>
              </details>

              <details className="group bg-gray-50 rounded-xl p-6 cursor-pointer border border-gray-200 hover:border-teal-300 transition-all">
                <summary className="flex justify-between items-center font-semibold text-gray-900 text-lg list-none">
                  <span>Sistem Nasıl İşler?</span>
                  <i className="ri-arrow-down-s-line text-2xl transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  Sisteme kayıt oldukdan sonra sizin adınıza tasarlanan özel sticker'lar otelinizin adresin kargolanır. Bu QR Kodlu sticker'ları odalarınızda uygun yerlere yapıştırın (Örneğin: Ayna, Kapı, Buz Dolabı). Misafir WIFI Bağlantısı için QR Kodu okutur WIFI Ağınıza bağlanır ve Yapay Zeka Asistan kendini tanıtır. Bundan sonra Yapay Zeka Asistan tüm süreci sizin adınıza yönetir ve 7/24 misafirlerinize iletişimde kalır.
                </p>
              </details>
            </div>

            <div className="mt-12 text-center p-8 bg-teal-50 rounded-2xl border border-teal-200">
              <h3 className="text-2xl font-black text-gray-900 mb-4">Başka sorunuz mu var?</h3>
              <p className="text-gray-600 mb-6">Destek ekibimiz size yardımcı olmak için burada!</p>
              <button onClick={() => scrollToSection('contact')} className="inline-block px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition font-bold shadow-lg shadow-teal-600/20 cursor-pointer whitespace-nowrap">
                Bize Ulaşın
              </button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-teal-900 text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/f353b4d7-dacd-40f7-a8e4-5449d615ae6c.png" alt="Hotel Yapay Zeka Çözümleri Logo" className="h-10 w-auto" />
              </div>
              <p className="text-teal-200 text-sm leading-relaxed mb-6">
                Oteller için geliştirilmiş yeni nesil <strong>yapay zeka asistanı</strong>. Misafir memnuniyetini artırın, maliyetleri düşürün.
              </p>
              <div className="flex gap-4">
                <img src="https://hotelai.io/storage/google-play.png" alt="Google Play'den İndir" title="Google Play" className="h-10 w-auto cursor-pointer" />
                <img src="https://hotelai.io/storage/apple-store.png" alt="App Store'dan İndir" title="App Store" className="h-10 w-auto cursor-pointer" />
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-lg"><a href="/apps" className="hover:text-teal-300 transition-colors">Uygulamalar</a></h4>
              <ul className="space-y-3 text-sm text-teal-200">
                <li><a href="/apps" className="hover:text-white transition-colors cursor-pointer">Yapay Zeka Asistan</a></li>
                <li><a href="/apps" className="hover:text-white transition-colors cursor-pointer">OTA Ranker</a></li>
                <li><a href="/apps" className="hover:text-white transition-colors cursor-pointer">Rakip Analizi</a></li>
                <li><a href="/apps" className="hover:text-white transition-colors cursor-pointer">İtibar Yönetimi</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-lg"><a href="#" className="hover:text-teal-300 transition-colors">Şirket</a></h4>
              <ul className="space-y-3 text-sm text-teal-200">
                <li><a href="https://yapayzekacozumleri.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Hakkımızda</a></li>
                <li><button onClick={() => setPrivacyPolicyOpen(true)} className="hover:text-white transition-colors cursor-pointer">Gizlilik Politikası</button></li>
                <li><button onClick={() => setTermsOfServiceOpen(true)} className="hover:text-white transition-colors cursor-pointer">Hizmet Şartları</button></li>
                <li><button onClick={() => setKvkkOpen(true)} className="hover:text-white transition-colors cursor-pointer">KVKK</button></li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-6 text-lg"><a href="#contact" className="hover:text-teal-300 transition-colors">İletişim</a></h4>
              <address className="not-italic">
                <ul className="space-y-3 text-sm text-teal-200">
                  <li>
                    <a href="mailto:yapayzekacozumlerim@gmail.com" className="hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                      <i className="ri-mail-line" aria-hidden="true"></i>
                      yapayzekacozumlerim@gmail.com
                    </a>
                  </li>
                  <li>
                    <a href="https://yapayzekacozumleri.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                      <i className="ri-global-line" aria-hidden="true"></i>
                      yapayzekacozumleri.com
                    </a>
                  </li>
                  <li>
                    <a href="https://hotelyapayzekacozumleri.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                      <i className="ri-global-line" aria-hidden="true"></i>
                      hotelyapayzekacozumleri.com
                    </a>
                  </li>
                  <li>
                    <a href="tel:+905542367386" className="hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                      <i className="ri-phone-line" aria-hidden="true"></i>
                      0 554 236 73 86
                    </a>
                  </li>
                </ul>
              </address>
            </div>
          </div>

          <div className="border-t border-teal-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-teal-300 text-sm">
              © 2025 Hotel Yapay Zeka Çözümleri. Tüm hakları saklıdır.
            </p>
            <div className="flex gap-6 text-sm text-teal-300">
              <span>Made with ❤️ in Türkiye 🇹🇷</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="nofollow noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {privacyPolicyOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPrivacyPolicyOpen(false)}></div>
            <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-2xl font-bold text-gray-900">Gizlilik Politikası</h2>
                <button 
                  onClick={() => setPrivacyPolicyOpen(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="p-6 space-y-8">
                <div className="text-sm text-gray-600 mb-6">
                  <strong>Son güncelleme:</strong> 24.01.2026
                </div>

                <div className="prose prose-gray max-w-none">
                  <p className="text-gray-700 leading-relaxed">
                    Bu Gizlilik Politikası, Meta/WhatsApp Business API kullanım gereklilikleri ve Türkiye Cumhuriyeti 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) uyarınca hazırlanmıştır.
                  </p>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Veri Toplama Amacı</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Kişisel verileriniz yalnızca ve yalnızca Yapay Zeka hizmetini sunmak için toplanır:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-user-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Otel-Misafir İletişimi:</strong> WhatsApp üzerinden otomatik mesajlaşma</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-phone-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Otomasyon:</strong> Hoş geldin, veda, doğum günü mesajları</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-calendar-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Planlı Mesajlar:</strong> Konaklama süresi bilgisi</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-global-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>IP Adresi:</strong> Güvenlik ve dolandırıcılık önleme</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. Üçüncü Taraf Paylaşımı</h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-2">Meta/WhatsApp</h4>
                      <p className="text-sm text-gray-700 mb-2"><strong>Amaç:</strong> Mesaj gönderme altyapısı</p>
                      <p className="text-sm text-gray-700"><strong>Paylaşılan:</strong> Telefon numaraları, mesaj içerikleri</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-bold text-gray-900 mb-2">TourComp</h4>
                      <p className="text-sm text-gray-700 mb-2"><strong>Amaç:</strong> Komisyonlu satışlar için yönlendirme</p>
                      <p className="text-sm text-gray-700 mb-2"><strong>Paylaşılan:</strong> SADECE anonimleştirilmiş trafik veya açık izinli yönlendirme</p>
                      <p className="text-sm text-red-600"><strong>Kişisel bilgiler (ad, telefon) paylaşılmaz</strong></p>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Veri Saklama Süresi</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-time-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Mesajlar ve Misafir Verileri:</strong> Otel üyeliği süresince saklanır</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-delete-bin-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Üyelik İptali Sonrası:</strong> 30 gün yedeklerde tutulur, ardından kalıcı silinir</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Kullanıcı Hakları (KVKK)</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    6698 sayılı KVKK kapsamında haklarınız:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-search-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerinizin işlenip işlenmediğini öğrenme:</strong> Bu bilgiyi KVKK'nin 6698/6/1 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-edit-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerin düzeltilmesini isteme:</strong> Bu bilgiyi KVKK'nin 6698/6/2 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-delete-bin-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerin silinmesini isteme:</strong> Bu bilgiyi KVKK'nin 6698/6/3 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
                <button 
                  onClick={() => setPrivacyPolicyOpen(false)}
                  className="w-full h-12 px-6 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold transition-all cursor-pointer whitespace-nowrap"
                >
                  Anladım
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {termsOfServiceOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setTermsOfServiceOpen(false)}></div>
            <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-2xl font-bold text-gray-900">Hizmet Şartları ve Kullanım Koşulları</h2>
                <button 
                  onClick={() => setTermsOfServiceOpen(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="p-6 space-y-8">
                <div className="text-sm text-gray-600 mb-6">
                  <strong>Son güncelleme:</strong> 24.01.2026
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Hizmet Tanımı</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Yapay Zeka, WhatsApp üzerinden yapay zeka destekli otel misafir iletişim hizmeti sunar:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>7/24 otomatik misafir desteği</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Planlı mesaj gönderimi (hoş geldin, veda, doğum günü)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Acil durum yönetimi ve görev atama</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-check-circle-fill text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Dashboard ve raporlama</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. Sorumluluk Sınırı</h3>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mb-4">
                    <p className="text-amber-800 font-semibold mb-2">
                      <i className="ri-alert-line mr-2"></i>
                      ÖNEMLİ: Yapay Zeka bir otomasyon aracıdır.
                    </p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Yapay zeka yanıtlarının doğruluğundan sorumlu değiliz</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Hizmet kalitesinden kaynaklanan kayıplar otele aittir</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-information-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>WhatsApp API kullanım şartlarını ihlal eden davranışlar</span>
                    </li>
                  </ul>
                  <p className="text-sm text-red-600 mt-4">
                    <strong>Uyarı:</strong> Bu durumda Meta API erişiminiz askıya alınabilir.
                  </p>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Fiyatlandırma</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-money-dollar-circle-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Uygulama Aboneliği:</strong> Aylık paket ücreti</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-message-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Mesaj Maliyeti:</strong> Meta'ya ödenecek mesaj başına ücret otele aittir</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-bank-card-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Ödeme:</strong> Kredi kartı ile otomatik yenilenir</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-close-circle-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>İptal:</strong> İstediğiniz zaman iptal edebilirsiniz (kısmi iade yok)</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">5. Kullanıcı Sorumlulukları</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-user-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Doğru ve güncel bilgiler sağlamak</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-shield-check-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Hesap güvenliğini korumak</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-scales-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>Yasalara ve düzenlemelere uymak</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-whatsapp-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span>WhatsApp Business API kullanım koşullarına uymak</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
                <button 
                  onClick={() => setTermsOfServiceOpen(false)}
                  className="w-full h-12 px-6 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold transition-all cursor-pointer whitespace-nowrap"
                >
                  Anladım
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KVKK Modal */}
      {kvkkOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setKvkkOpen(false)}></div>
            <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between rounded-t-2xl">
                <h2 className="text-2xl font-bold text-gray-900">Kişisel Verilerin Korunması Hakkında Aydınlatma Metni</h2>
                <button 
                  onClick={() => setKvkkOpen(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="p-6 space-y-8">
                <div className="text-sm text-gray-600 mb-6">
                  <strong>6698 sayılı Kişisel Verilerin Korunması Kanunu uyarınca</strong>
                </div>

                <div className="prose prose-gray max-w-none">
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">1. Veri Sorumlusu</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-building-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Veri İşleyen:</strong> Hotel Yapay Zeka Çözümleri (Teknik altyapı sağlayıcı)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-hotel-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Asıl Veri Sorumlusu:</strong> Otel (Misafir verilerinin sahibi)</span>
                    </li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Hotel Yapay Zeka Çözümleri, oteller adına veri işleyen konumundadır. Misafir verilerinin asıl sorumlusu oteldir.
                  </p>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">2. İşleme Sebebi</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Kişisel verileriniz aşağıdaki sebeplerle işlenir:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong className="text-gray-900">Açık Rıza (KVKK m.5)</strong>
                        <p className="text-sm text-gray-600">QR Check-in formunda KVKK onayı alınarak işlenir</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-scales-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <div>
                        <strong className="text-gray-900">Yasal Zorunluluk (KVKK m.5/2-ç)</strong>
                        <p className="text-sm text-gray-600">Konaklama hizmeti sunma yükümlülüğü kapsamında</p>
                      </div>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">3. İşlenen Kişisel Veriler</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-user-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kimlik Bilgileri:</strong> Ad, Soyad</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-phone-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>İletişim Bilgileri:</strong> Telefon numarası</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-hotel-bed-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Konaklama Bilgileri:</strong> Check-in/out tarihleri, oda numarası</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-message-3-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>İletişim Kayıtları:</strong> WhatsApp mesaj içerikleri</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">4. Veri Aktarımı</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Kişisel verileriniz aşağıdaki üçüncü taraflara aktarılabilir:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-whatsapp-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Meta/WhatsApp:</strong> Mesajlaşma altyapısı (Yurt dışı aktarım)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-robot-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>OpenAI:</strong> Yapay zeka hizmeti (Yurt dışı aktarım)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-government-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Yasal Merciler:</strong> Yasal zorunluluk halinde</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">6. Haklarınız (KVKK)</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    6698 sayılı KVKK kapsamında haklarınız:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <i className="ri-search-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerinizin işlenip işlenmediğini öğrenme:</strong> Bu bilgiyi KVKK'nin 6698/6/1 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-edit-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerin düzeltilmesini isteme:</strong> Bu bilgiyi KVKK'nin 6698/6/2 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <i className="ri-delete-bin-line text-teal-600 mt-1 flex-shrink-0"></i>
                      <span><strong>Kişisel verilerin silinmesini isteme:</strong> Bu bilgiyi KVKK'nin 6698/6/3 numaralı maddesinde bulabilirsiniz.</span>
                    </li>
                  </ul>

                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4">7. İmha Süresi</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Kişisel verileriniz, kullanım amacı ortadan kalktıktan sonra ve yasal saklama süreleri sona erdiğinde imha edilecektir.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-gray-700">
                      <strong>Konaklama sonrası:</strong> Veriler otel üyeliği süresince saklanır, üyelik iptali sonrası 30 gün içinde silinir.
                    </p>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 rounded-b-2xl">
                <button 
                  onClick={() => setKvkkOpen(false)}
                  className="w-full h-12 px-6 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-bold transition-all cursor-pointer whitespace-nowrap"
                >
                  Anladım
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
